'use client';

/**
 * OrgUsageRatesPanel — one organization's usage and AI rates next to its plan.
 *
 * Rates are created automatically from the plan's rate card when a plan is
 * assigned (backend services/tier_rate_card_bridge.py). From here an operator can:
 *   • Waive a rate   — bill $0 while still tracking usage (Restore puts it back)
 *   • Remove a rate  — stop billing it; a later plan change won't re-create it
 *   • Reset to plan  — replace one rate (or all) with the plan's rate card
 *   • Apply plan rates — fill in any channel the org is missing
 * Editing a rate's numbers is done in the tables below (Edit); edited rates are
 * marked "Edited" and plan changes leave them alone.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Ban, Building2, Loader2, RefreshCw, RotateCcw, Trash2, Undo2, Wand2 } from 'lucide-react';

type FetchAPI = <T>(path: string, options?: RequestInit) => Promise<T>;

interface PlanRate {
  channel: string;
  channel_display_name: string;
  unit_name: string;
  overage_rate: number;
  included_units_per_month: number;
  included_period: 'month' | 'year';
}

interface AiPlanRate {
  service_code: string;
  service_name: string;
  input_rate_per_million: number;
  output_rate_per_million: number;
  included_credits_per_month: number;
}

type RateStatus = 'active' | 'waived' | 'removed';
type RateSource = 'tier' | 'admin' | 'manual';

interface CommRate {
  id: string;
  channel: string;
  channel_display_name: string | null;
  unit_name: string;
  overage_rate: number | null;
  cost_per_unit: number;
  included_units_per_month: number;
  included_period: 'month' | 'year';
  status: RateStatus;
  source: RateSource;
}

interface AiRate {
  id: string;
  service_code: string;
  service_name: string;
  input_rate_per_million: number;
  output_rate_per_million: number;
  included_credits_per_month: number;
  status: RateStatus;
  source: RateSource;
}

interface OrgRates {
  plan: { tier_code: string; tier_name: string; rate_card: PlanRate[]; ai_rate_card: AiPlanRate | null } | null;
  communication_rates: CommRate[];
  ai_rates: AiRate[];
}

const money = (v: number | null | undefined, digits = 3) =>
  v == null ? '—' : `$${Number(v).toFixed(digits).replace(/0+$/, '').replace(/\.$/, '.00')}`;
const allowance = (n: number, period: string, unit: string) =>
  n ? `${n.toLocaleString()} ${unit}s/${period === 'year' ? 'yr' : 'mo'}` : 'None';

const STATUS_STYLE: Record<RateStatus, string> = {
  active: 'bg-green-50 text-green-700',
  waived: 'bg-amber-50 text-amber-700',
  removed: 'bg-gray-100 text-gray-500',
};
const SOURCE_LABEL: Record<RateSource, string> = { tier: 'From plan', admin: 'Edited', manual: 'Set manually' };

export default function OrgUsageRatesPanel({
  fetchAPI,
  orgs,
  onChanged,
}: {
  fetchAPI: FetchAPI;
  orgs: { id: number; name: string }[];
  onChanged: () => void;
}) {
  const [cboId, setCboId] = useState<number | null>(null);
  const [data, setData] = useState<OrgRates | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!cboId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await fetchAPI<OrgRates>(`/org-usage-rates/${cboId}`));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [cboId, fetchAPI]);

  useEffect(() => {
    setData(null);
    setNotice(null);
    load();
  }, [load]);

  const run = async (key: string, path: string, init: RequestInit, done: string) => {
    setBusy(key);
    setError(null);
    setConfirming(null);
    try {
      await fetchAPI(path, init);
      setNotice(done);
      await load();
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const applyPlan = (reset: boolean, channels?: string[]) =>
    run(
      `apply:${reset}:${channels?.join(',') || 'all'}`,
      `/org-usage-rates/${cboId}/apply-plan`,
      { method: 'POST', body: JSON.stringify({ reset, channels: channels || null }) },
      reset ? 'Reset to the plan’s rates.' : 'Applied the plan’s rates to missing channels.',
    );
  const waive = (kind: 'communication' | 'ai-token', id: string, waiveIt: boolean) =>
    run(
      `waive:${id}`,
      `/${kind}-pricing/${id}/waive`,
      { method: 'POST', body: JSON.stringify({ waive: waiveIt }) },
      waiveIt ? 'Waived. Usage is still tracked and billed at $0.' : 'Restored the rate.',
    );
  const remove = (kind: 'communication' | 'ai-token', id: string) =>
    run(`remove:${id}`, `/${kind}-pricing/${id}`, { method: 'DELETE' }, 'Removed. This channel is no longer billed.');

  const planByChannel = Object.fromEntries((data?.plan?.rate_card || []).map((r) => [r.channel, r]));
  const commByChannel: Record<string, CommRate> = {};
  for (const r of data?.communication_rates || []) {
    // Prefer the active/waived row over a removed one for the same channel.
    if (!commByChannel[r.channel] || commByChannel[r.channel].status === 'removed') commByChannel[r.channel] = r;
  }
  const channels = Array.from(new Set([...Object.keys(planByChannel), ...Object.keys(commByChannel)]));
  const aiPlan = data?.plan?.ai_rate_card || null;
  const aiRate =
    (data?.ai_rates || []).find((r) => r.status !== 'removed') || (data?.ai_rates || [])[0] || null;

  const actionButtons = (kind: 'communication' | 'ai-token', rate: CommRate | AiRate | null, channel: string) => {
    if (!rate) {
      return planHas(channel) ? (
        <button onClick={() => applyPlan(false, [channel])} disabled={!!busy} className="text-indigo-600 hover:underline text-xs">
          Add from plan
        </button>
      ) : null;
    }
    if (rate.status === 'removed') {
      return planHas(channel) ? (
        <button onClick={() => applyPlan(true, [channel])} disabled={!!busy} className="inline-flex items-center gap-1 text-indigo-600 hover:underline text-xs">
          <Undo2 className="w-3 h-3" /> Restore from plan
        </button>
      ) : null;
    }
    const removeKey = `remove:${rate.id}`;
    return (
      <div className="flex items-center justify-end gap-3 text-xs">
        {rate.status === 'waived' ? (
          <button onClick={() => waive(kind, rate.id, false)} disabled={!!busy} className="inline-flex items-center gap-1 text-amber-700 hover:underline">
            <RotateCcw className="w-3 h-3" /> Restore
          </button>
        ) : (
          <button onClick={() => waive(kind, rate.id, true)} disabled={!!busy} className="inline-flex items-center gap-1 text-gray-600 hover:text-amber-700" title="Bill $0, keep tracking usage">
            <Ban className="w-3 h-3" /> Waive
          </button>
        )}
        {planHas(channel) && rate.source !== 'tier' && (
          <button onClick={() => applyPlan(true, [channel])} disabled={!!busy} className="inline-flex items-center gap-1 text-gray-600 hover:text-indigo-600" title="Replace with the plan's rate">
            <RefreshCw className="w-3 h-3" /> Reset to plan
          </button>
        )}
        {confirming === removeKey ? (
          <span className="inline-flex items-center gap-2">
            <button onClick={() => remove(kind, rate.id)} disabled={!!busy} className="text-red-600 font-medium hover:underline">Confirm remove</button>
            <button onClick={() => setConfirming(null)} className="text-gray-500 hover:underline">Cancel</button>
          </span>
        ) : (
          <button onClick={() => setConfirming(removeKey)} disabled={!!busy} className="inline-flex items-center gap-1 text-gray-600 hover:text-red-600">
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
      </div>
    );
  };
  const planHas = (channel: string) => (channel === 'ai' ? !!aiPlan : !!planByChannel[channel]);

  const badges = (rate: CommRate | AiRate | null) =>
    rate ? (
      <div className="flex flex-wrap gap-1">
        <span className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLE[rate.status]}`}>
          {rate.status === 'active' ? 'Billing' : rate.status === 'waived' ? 'Waived' : 'Removed'}
        </span>
        {rate.status !== 'removed' && (
          <span className="px-2 py-0.5 rounded text-xs bg-gray-50 text-gray-600">{SOURCE_LABEL[rate.source] || rate.source}</span>
        )}
      </div>
    ) : (
      <span className="px-2 py-0.5 rounded text-xs bg-gray-50 text-gray-500">Not set</span>
    );

  return (
    <section className="bg-white border rounded-xl p-4 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600" />
            Organization rates
          </h2>
          <p className="text-sm text-gray-500 max-w-2xl">
            Assigning a plan sets the organization's usage and AI rates from the plan's rate card. Waive a rate to bill $0 while
            still tracking usage, remove it to stop billing it, or reset it to the plan.
          </p>
        </div>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1">Organization</span>
          <select
            id="org-usage-rates-org"
            value={cboId ?? ''}
            onChange={(e) => setCboId(e.target.value ? Number(e.target.value) : null)}
            className="border rounded-lg px-3 py-1.5 min-w-[16rem]"
          >
            <option value="">Select an organization…</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {notice && !error && <p className="text-sm text-green-700">{notice}</p>}

      {!cboId ? (
        <p className="text-sm text-gray-500">Select an organization to see its rates.</p>
      ) : loading && !data ? (
        <div className="flex items-center text-sm text-gray-500"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading rates…</div>
      ) : data && !data.plan ? (
        <p className="text-sm text-gray-500">
          This organization has no active plan. Assign a plan first; its rates are set from the plan's rate card.
        </p>
      ) : data ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <span className="text-gray-700">
              Plan: <span className="font-medium">{data.plan!.tier_name}</span>
              {!data.plan!.rate_card.length && !aiPlan && <span className="text-gray-500"> · this plan has no rate card</span>}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => applyPlan(false)}
                disabled={!!busy || (!data.plan!.rate_card.length && !aiPlan)}
                className="inline-flex items-center px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm disabled:bg-gray-300"
              >
                {busy?.startsWith('apply:false') ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
                Apply plan rates
              </button>
              {confirming === 'reset-all' ? (
                <span className="inline-flex items-center gap-2 text-sm">
                  <button onClick={() => applyPlan(true)} disabled={!!busy} className="text-red-600 font-medium hover:underline">
                    Replace all rates with the plan's
                  </button>
                  <button onClick={() => setConfirming(null)} className="text-gray-500 hover:underline">Cancel</button>
                </span>
              ) : (
                <button
                  onClick={() => setConfirming('reset-all')}
                  disabled={!!busy || (!data.plan!.rate_card.length && !aiPlan)}
                  className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm disabled:opacity-50"
                >
                  <RefreshCw className="w-4 h-4 mr-1" /> Reset all to plan
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Channel</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Plan rate</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">This organization</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {channels.map((ch) => {
                  const plan = planByChannel[ch];
                  const rate = commByChannel[ch] || null;
                  return (
                    <tr key={ch}>
                      <td className="px-3 py-2 font-medium">{plan?.channel_display_name || rate?.channel_display_name || ch}</td>
                      <td className="px-3 py-2 text-gray-600">
                        {plan ? <>{allowance(plan.included_units_per_month, plan.included_period, plan.unit_name)} · then {money(plan.overage_rate)}/{plan.unit_name}</> : <span className="text-gray-400">Not in plan</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-800">
                        {rate && rate.status !== 'removed'
                          ? <>{allowance(rate.included_units_per_month, rate.included_period, rate.unit_name)} · then {money(rate.overage_rate ?? rate.cost_per_unit)}/{rate.unit_name}</>
                          : <span className="text-gray-400">Not billed</span>}
                      </td>
                      <td className="px-3 py-2">{badges(rate)}</td>
                      <td className="px-3 py-2 text-right">{actionButtons('communication', rate, ch)}</td>
                    </tr>
                  );
                })}
                {(aiPlan || aiRate) && (
                  <tr>
                    <td className="px-3 py-2 font-medium">AI features</td>
                    <td className="px-3 py-2 text-gray-600">
                      {aiPlan
                        ? <>{money(aiPlan.included_credits_per_month, 2)}/mo included · then {money(aiPlan.input_rate_per_million, 2)} / {money(aiPlan.output_rate_per_million, 2)} per 1M tokens in/out</>
                        : <span className="text-gray-400">Not in plan</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-800">
                      {aiRate && aiRate.status !== 'removed'
                        ? <>{money(aiRate.included_credits_per_month, 2)}/mo included · then {money(aiRate.input_rate_per_million, 2)} / {money(aiRate.output_rate_per_million, 2)} per 1M</>
                        : <span className="text-gray-400">Not billed</span>}
                    </td>
                    <td className="px-3 py-2">{badges(aiRate)}</td>
                    <td className="px-3 py-2 text-right">{actionButtons('ai-token', aiRate, 'ai')}</td>
                  </tr>
                )}
                {!channels.length && !aiPlan && !aiRate && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">No usage rates for this organization yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">
            To change a rate's numbers, use Edit on the organization's row in the tables below. Edited rates are kept when the plan changes.
          </p>
        </>
      ) : null}
    </section>
  );
}
